import { useEffect, useId, useRef, useState, type DragEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { STORE_SETUP_LIMITS, type StoreSetupStatus, type StoreSetupView, type StoreThemeId } from "@townsy/contracts";
import { adKeys } from "../../api/advertisingQueries";
import { isApiError } from "../../api/client";
import { queryKeys, useBootstrapQuery } from "../../api/queries";
import { newStoreSetupKey, putStoreSetup, storeSetupKeys, useStoreSetupQuery } from "../../api/storeSetup";
import { useTownSession } from "../../app/TownSessionContext";
import { checkDestinationUrl } from "../../advertise/creativeModel";
import { isSameOriginApiImage } from "../../advertise/media";
import { UPLOAD_ACCEPT, UPLOAD_BUSY, type UploadState } from "../../advertise/uploadMachine";
import {
  STORE_BRAND_SWATCHES,
  STORE_CATEGORY_HINT,
  STORE_CATEGORY_OPTIONS,
  formFromReuse,
  formFromView,
  isStoreFormDirty,
  logoStatusText,
  reusedDesignUnchanged,
  reuseNote,
  savedMessage,
  storeReuseQuestion,
  storeLoadErrorMessage,
  storeMonogramOf,
  storeSaveErrorMessage,
  storeStatusCopy,
  toStoreRequest,
  validateStoreForm,
  type StoreFormErrors,
  type StoreFormField,
  type StoreSetupForm,
} from "../../storeSetup/storeSetupModel";
import { useTownStore } from "../../state/store";
import { useUpload, type UploadHandle } from "../advertising/UploadField";
import { Dialog } from "../Dialog";
import { StoreThemeThumbnail } from "../StoreThemeDialog";
import { STORE_THEME_OPTIONS } from "../storeThemeLogic";
import { StorePreview, type StorePreviewModel } from "./StorePreview";

/**
 * "Set up your store" / "Edit your store" (ADR-0020, founder feedback 2026-10-14): ONE screen for the current owner of a
 * store: business name, logo, website, short description, category, sign colour, the sign inside the store and the
 * interior, with a live preview painted by the town's own sign code and ONE Save. One plain status says what the town
 * shows. The page's single instance is opened from the purchase-complete toast, the checkout, the receipt, the store
 * panel ("Edit your store"), the directory's "My stores" and the 2D fallback. The server re-checks ownership and decides.
 *
 * ADR-0031 (founder 2026-10-20: "Yes, reuse the store design too"): while the store shows no design of the owner yet, the
 * screen offers "Re-use your store design from <business> (<old address>)?". One press fills it in; Save puts the
 * already-checked design on the store at once (changed, it is checked first). When the owner also has offers there
 * (ADR-0030), the offers screen then opens filled in too.
 */
export function StoreSetupHost() {
  const request = useTownStore((s) => s.storeSetup);
  if (!request) return null;
  return <StoreSetupLoader key={request.id} propertyId={request.propertyId} afterPurchase={request.source === "purchase"} reuse={request.reuse === true} />;
}

function StoreSetupLoader({ propertyId, afterPurchase, reuse }: { propertyId: string; afterPurchase: boolean; reuse: boolean }) {
  const close = useTownStore((s) => s.closeStoreSetup);
  const boot = useBootstrapQuery();
  const signedIn = Boolean(boot.data?.session.userId);
  const q = useStoreSetupQuery(signedIn ? propertyId : null);
  if (boot.isPending || (signedIn && q.isPending)) {
    return (
      <Dialog title="Your store" onClose={close} className="ss-dialog-small">
        <p className="muted" role="status" style={{ margin: 0 }} data-testid="store-setup-loading">
          Loading your store…
        </p>
      </Dialog>
    );
  }
  if (!signedIn || q.error || !q.data) {
    const message = !signedIn ? "Sign in with the account that owns this store to set it up." : storeLoadErrorMessage(q.error);
    const retry = signedIn && !(isApiError(q.error) && [401, 403, 404, 422].includes(q.error.status));
    return (
      <Dialog title="Your store" onClose={close} className="ss-dialog-small">
        <div className="stack" data-testid="store-setup-unavailable">
          <p className="notice notice-info" role="status" style={{ margin: 0 }}>
            {message}
          </p>
          <div className="row">
            {retry ? (
              <button type="button" className="btn btn-primary" onClick={() => void q.refetch()}>
                Try again
              </button>
            ) : null}
            {!signedIn ? (
              <Link className="btn btn-primary" to="/account" onClick={close}>
                Sign in
              </Link>
            ) : null}
            <button type="button" className="btn" onClick={close}>
              Close
            </button>
          </div>
        </div>
      </Dialog>
    );
  }
  return <StoreSetupEditor view={q.data} afterPurchase={afterPurchase} reuse={reuse} onClose={close} />;
}

function StoreSetupEditor({ view, afterPurchase, reuse, onClose }: { view: StoreSetupView; afterPurchase: boolean; reuse: boolean; onClose(): void }) {
  const qc = useQueryClient();
  const session = useTownSession();
  const pushToast = useTownStore((s) => s.pushToast);
  const openOfferSetup = useTownStore((s) => s.openOfferSetup);
  const [base, setBase] = useState<StoreSetupForm>(() => formFromView(view));
  // ADR-0031: opened from "Re-use your store design" (the notice, the panel): the screen starts filled in from it.
  const [form, setForm] = useState<StoreSetupForm>(() => (reuse && view.reuse ? formFromReuse(view) : base));
  const [touched, setTouched] = useState<Partial<Record<StoreFormField, boolean>>>({});
  const [showAll, setShowAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const upload = useUpload("logo");
  const keyRef = useRef<string | null>(null);

  const logoBusy = UPLOAD_BUSY.has(upload.state.phase);
  const newLogo = upload.state.phase === "ready" && upload.state.uploadId ? { uploadId: upload.state.uploadId, previewUrl: upload.state.view?.previewUrl ?? null } : null;
  const ctx = { logoBusy, hasSign: view.sign.version > 0 };
  const errors = validateStoreForm(form, ctx);
  const dirty = isStoreFormDirty(form, base, Boolean(newLogo));

  const refreshTown = () => {
    if (session) void session.refreshProperty(view.propertyId);
    void qc.invalidateQueries({ queryKey: queryKeys.property(view.propertyId) });
    void qc.invalidateQueries({ queryKey: queryKeys.properties });
    void qc.invalidateQueries({ queryKey: adKeys.myStores });
    void qc.invalidateQueries({ queryKey: adKeys.myCreatives });
    void qc.invalidateQueries({ queryKey: adKeys.myAdvertisers });
  };

  // The quick check (or a reviewer) finished while the screen is open: say so once and refresh the town.
  const lastState = useRef(view.status.state);
  useEffect(() => {
    const before = lastState.current;
    lastState.current = view.status.state;
    if (before === view.status.state) return;
    if (view.status.state === "live" && (before === "checking" || before === "waiting")) {
      pushToast(`Your store at ${view.address} is live. Everyone in town sees your new sign.`, "success");
      refreshTown();
    }
    // refreshTown is recreated on each render; the status change alone is the trigger.
  }, [view.status.state]);

  const change = <K extends keyof StoreSetupForm>(k: K, v: StoreSetupForm[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setResult(null);
    setConfirmClose(false);
    keyRef.current = null; // a different request needs a different Idempotency-Key
  };
  const touch = (f: StoreFormField) => setTouched((t) => (t[f] ? t : { ...t, [f]: true }));

  const save = async () => {
    setShowAll(true);
    if (saving) return;
    const body = toStoreRequest(form, view, { ...ctx, newLogoUploadId: newLogo?.uploadId ?? null });
    if (!body) {
      setResult({ tone: "error", text: logoBusy ? "Wait until your logo is ready, then save." : "Check the highlighted fields, then save again." });
      return;
    }
    keyRef.current ??= newStoreSetupKey();
    setSaving(true);
    setResult(null);
    try {
      const reused = form.reusedFrom ?? null;
      const res = await putStoreSetup(view.propertyId, body, keyRef.current);
      keyRef.current = null;
      qc.setQueryData(storeSetupKeys.one(view.propertyId), res.setup);
      const next = formFromView(res.setup);
      setBase(next);
      setForm(next);
      setTouched({});
      setShowAll(false);
      upload.reset();
      refreshTown();
      // ADR-0031 with ADR-0030: the re-used design is live, so the offers come next, filled in (one more press).
      if (reused && reused.offers > 0 && res.setup.status.state === "live") {
        pushToast(`${savedMessage(res.saved, res.setup.status)} Now your offers from ${reused.fromAddress}: tick the box and send them for review.`, "success");
        openOfferSetup(view.propertyId, "store", "reuse");
        return;
      }
      setResult({
        tone: "success",
        text: `${savedMessage(res.saved, res.setup.status)}${reused && reused.offers > 0 ? ` Once it is live, re-use your offers from ${reused.fromAddress} in your store's panel.` : ""}`,
      });
    } catch (err) {
      setResult({ tone: "error", text: storeSaveErrorMessage(err) });
      // A final refusal is answered the same for the same key: the next press is a new request.
      if (!(isApiError(err) && err.retryable)) keyRef.current = null;
    } finally {
      setSaving(false);
    }
  };

  const requestClose = () => {
    if (saving) return;
    if (dirty && !confirmClose) {
      setConfirmClose(true);
      return;
    }
    onClose();
  };

  return (
    <StoreSetupScreen
      view={view}
      form={form}
      errors={errors}
      visibleError={(f) => (showAll || touched[f] ? errors[f] : undefined)}
      upload={upload}
      newLogoPreview={newLogo?.previewUrl ?? null}
      afterPurchase={afterPurchase}
      saving={saving}
      result={result}
      confirmClose={confirmClose}
      onChange={change}
      onTouch={touch}
      onRemoveLogo={() => {
        upload.reset();
        change("logo", null);
      }}
      onReuse={() => {
        setForm(formFromReuse(view));
        setResult(null);
        setConfirmClose(false);
        keyRef.current = null;
      }}
      onSave={() => void save()}
      onClose={requestClose}
      onDiscard={onClose}
      onKeepEditing={() => setConfirmClose(false)}
    />
  );
}

// ------------------------------------------------------------------------------------------------- the screen

export interface StoreSetupScreenProps {
  view: StoreSetupView;
  form: StoreSetupForm;
  errors: StoreFormErrors;
  visibleError(f: StoreFormField): string | undefined;
  upload: Pick<UploadHandle, "state" | "start" | "cancel" | "retry">;
  /** The private preview of a new logo once it is ready. */
  newLogoPreview: string | null;
  afterPurchase: boolean;
  saving: boolean;
  result: { tone: "success" | "error"; text: string } | null;
  confirmClose: boolean;
  onChange<K extends keyof StoreSetupForm>(k: K, v: StoreSetupForm[K]): void;
  onTouch(f: StoreFormField): void;
  onRemoveLogo(): void;
  /** ADR-0031: "Re-use this design" (fills the form; nothing is saved until Save). */
  onReuse?(): void;
  onSave(): void;
  onClose(): void;
  onDiscard(): void;
  onKeepEditing(): void;
}

/** The whole screen (stateless; server-rendered in unit tests). */
export function StoreSetupScreen(p: StoreSetupScreenProps) {
  const { view, form } = p;
  const id = useId();
  const statusCopy = storeStatusCopy(view.status);
  const setUp = view.status.state !== "not_set_up";
  const logoUrl = p.newLogoPreview ?? (form.logo && isSameOriginApiImage(form.logo.previewUrl) ? form.logo.previewUrl : null);
  const site = checkDestinationUrl(form.website);
  const preview: StorePreviewModel = {
    businessName: form.businessName,
    monogram: storeMonogramOf(form.businessName),
    brandColor: /^#[0-9a-fA-F]{6}$/.test(form.brandColor) ? form.brandColor : view.brandColor,
    accentColor: form.reusedFrom?.accentColor ?? view.accentColor,
    logoUrl,
    description: form.description,
    websiteHost: site.ok && !site.empty ? site.host : null,
    signTitle: form.signTitle,
    signMessage: form.signMessage,
  };
  const err = p.visibleError;
  const hint = (f: StoreFormField) => `${id}-${f}-hint`;
  const customColour = !(STORE_BRAND_SWATCHES as readonly string[]).includes(form.brandColor.toUpperCase()) && /^#[0-9a-fA-F]{6}$/.test(form.brandColor);
  const footer = (
    <div className="ss-footer">
      {p.result && !p.confirmClose ? (
        <div className={`ss-result ss-result-${p.result.tone}`} role={p.result.tone === "error" ? "alert" : "status"} data-testid="store-setup-result">
          {p.result.text}
        </div>
      ) : p.confirmClose ? (
        <div className="ss-discard" role="alert" data-testid="store-setup-discard">
          <span>Close without saving your changes?</span>
          <button type="button" className="btn btn-sm btn-danger" onClick={p.onDiscard} data-testid="store-setup-discard-yes">
            Discard changes
          </button>
          <button type="button" className="btn btn-sm" onClick={p.onKeepEditing}>
            Keep editing
          </button>
        </div>
      ) : (
        <span className="ss-footer-note hint" data-testid="store-setup-footer-note">
          {reusedDesignUnchanged(form, view, p.newLogoPreview ? "new-upload" : null)
            ? "Your design was already checked: Save puts it up right away."
            : view.quickCheck
              ? "Your name, logo and website get a quick check (usually under a minute)."
              : "A Townsy reviewer looks at your name, logo and website first."}{" "}
          The sign inside and the interior change at once.
        </span>
      )}
      <div className="ss-footer-actions">
        <button type="button" className="btn" onClick={p.onClose} disabled={p.saving}>
          Close
        </button>
        <button type="button" className="btn btn-primary btn-lg" onClick={p.onSave} disabled={p.saving} data-testid="store-setup-save">
          {p.saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
  return (
    <Dialog
      title={
        <span className="ss-title">
          {setUp ? "Edit your store" : "Set up your store"}
          <span className="ss-title-address">{view.address}</span>
        </span>
      }
      onClose={p.onClose}
      wide
      className="ss-dialog"
      describedBy={`${id}-status`}
      footer={footer}
    >
      <div className="ss" data-testid="store-setup" data-status={view.status.state}>
        {p.afterPurchase && !setUp ? (
          <p className="ss-lead" data-testid="store-setup-lead">
            Congratulations on your new store! Add your name, logo and website on this one screen, then press Save.
          </p>
        ) : null}
        <StatusBanner id={`${id}-status`} status={view.status} copy={statusCopy} />
        {view.reuse ? <ReuseDesignBanner view={view} form={form} newLogoUploadId={p.newLogoPreview ? "new" : null} disabled={p.saving} onReuse={p.onReuse} /> : null}

        <div className="ss-layout">
          <div className="ss-form">
            <section className="ss-card-section" aria-labelledby={`${id}-biz`}>
              <h3 id={`${id}-biz`} className="ss-h">
                Your business
              </h3>
              <label className="field">
                <span>
                  Business name <span className="ss-req">required</span>
                </span>
                <input
                  type="text"
                  value={form.businessName}
                  maxLength={STORE_SETUP_LIMITS.businessName * 2}
                  autoComplete="organization"
                  onChange={(e) => p.onChange("businessName", e.target.value)}
                  onBlur={() => p.onTouch("businessName")}
                  aria-invalid={Boolean(err("businessName"))}
                  aria-describedby={hint("businessName")}
                  data-testid="store-setup-name"
                />
                <FieldHint id={hint("businessName")} error={err("businessName")}>
                  On your sign, in your store's panel and in the directory. {Array.from(form.businessName).length}/{STORE_SETUP_LIMITS.businessName}
                </FieldHint>
              </label>

              <label className="field">
                <span>
                  Category <span className="ss-req">required</span>
                </span>
                <select
                  value={form.category}
                  onChange={(e) => p.onChange("category", e.target.value as StoreSetupForm["category"])}
                  onBlur={() => p.onTouch("category")}
                  aria-invalid={Boolean(err("category"))}
                  aria-describedby={hint("category")}
                  data-testid="store-setup-category"
                >
                  <option value="">Choose a category…</option>
                  {STORE_CATEGORY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <FieldHint id={hint("category")} error={err("category")}>
                  {STORE_CATEGORY_HINT}
                </FieldHint>
              </label>

              <label className="field">
                <span>
                  Short description <span className="ss-opt">optional</span>
                </span>
                <textarea
                  value={form.description}
                  rows={3}
                  maxLength={STORE_SETUP_LIMITS.description * 2}
                  onChange={(e) => p.onChange("description", e.target.value)}
                  onBlur={() => p.onTouch("description")}
                  aria-invalid={Boolean(err("description"))}
                  aria-describedby={hint("description")}
                  data-testid="store-setup-description"
                />
                <FieldHint id={hint("description")} error={err("description")}>
                  Shown in your store's panel and in the directory. {Array.from(form.description).length}/{STORE_SETUP_LIMITS.description}
                </FieldHint>
              </label>

              <label className="field">
                <span>
                  Website <span className="ss-opt">optional</span>
                </span>
                <input
                  type="url"
                  inputMode="url"
                  autoComplete="url"
                  placeholder="https://yourbusiness.com"
                  value={form.website}
                  onChange={(e) => p.onChange("website", e.target.value)}
                  onBlur={() => p.onTouch("website")}
                  aria-invalid={Boolean(err("website"))}
                  aria-describedby={hint("website")}
                  data-testid="store-setup-website"
                />
                <FieldHint id={hint("website")} error={err("website")}>
                  {site.ok && !site.empty ? `Visitors see ${site.host} and go there only when they choose “Visit website”.` : "A secure https:// address. Visitors go there only when they choose “Visit website”."}
                </FieldHint>
              </label>
            </section>

            <section className="ss-card-section" aria-labelledby={`${id}-logo`}>
              <h3 id={`${id}-logo`} className="ss-h">
                Logo and colour
              </h3>
              <LogoDrop upload={p.upload} keptUrl={form.logo && !p.newLogoPreview ? form.logo.previewUrl : null} newUrl={p.newLogoPreview} onRemove={p.onRemoveLogo} error={err("logo")} disabled={p.saving} />
              <fieldset className="ss-swatches" aria-describedby={hint("brandColor")}>
                <legend>Sign colour</legend>
                <div className="ss-swatch-row">
                  {STORE_BRAND_SWATCHES.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`ss-swatch${form.brandColor.toUpperCase() === c ? " is-on" : ""}`}
                      style={{ background: c }}
                      aria-label={`Sign colour ${c}`}
                      aria-pressed={form.brandColor.toUpperCase() === c}
                      onClick={() => p.onChange("brandColor", c)}
                    />
                  ))}
                  <label
                    className={`ss-swatch ss-swatch-custom${customColour ? " is-on" : ""}`}
                    title="Any colour"
                    style={customColour ? { background: form.brandColor } : undefined}
                    data-testid="store-setup-colour-custom"
                  >
                    <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(form.brandColor) ? form.brandColor.toLowerCase() : "#2851d9"} onChange={(e) => p.onChange("brandColor", e.target.value.toUpperCase())} aria-label="Choose any sign colour" />
                  </label>
                </div>
                <FieldHint id={hint("brandColor")} error={err("brandColor")}>
                  The background of your name-only sign and the frame around your logo.
                </FieldHint>
              </fieldset>
            </section>

            <section className="ss-card-section" aria-labelledby={`${id}-inside`}>
              <h3 id={`${id}-inside`} className="ss-h">
                Inside your store
              </h3>
              <label className="field">
                <span>Sign inside: title</span>
                <input
                  type="text"
                  value={form.signTitle}
                  maxLength={STORE_SETUP_LIMITS.signTitle * 2}
                  placeholder={form.businessName.trim() || "Welcome!"}
                  onChange={(e) => p.onChange("signTitle", e.target.value)}
                  onBlur={() => p.onTouch("signTitle")}
                  aria-invalid={Boolean(err("signTitle"))}
                  aria-describedby={hint("signTitle")}
                  data-testid="store-setup-sign-title"
                />
                <FieldHint id={hint("signTitle")} error={err("signTitle")}>
                  The big line on the TV inside your store. {Array.from(form.signTitle).length}/{STORE_SETUP_LIMITS.signTitle}
                </FieldHint>
              </label>
              <label className="field">
                <span>Sign inside: message</span>
                <textarea
                  value={form.signMessage}
                  rows={3}
                  maxLength={STORE_SETUP_LIMITS.signMessage * 2}
                  placeholder="Today's offer, opening hours, what you sell…"
                  onChange={(e) => p.onChange("signMessage", e.target.value)}
                  onBlur={() => p.onTouch("signMessage")}
                  aria-invalid={Boolean(err("signMessage"))}
                  aria-describedby={hint("signMessage")}
                  data-testid="store-setup-sign-message"
                />
                <FieldHint id={hint("signMessage")} error={err("signMessage")}>
                  Plain text; links are not clickable. Visitors inside see it as soon as you save. {Array.from(form.signMessage).length}/{STORE_SETUP_LIMITS.signMessage}
                </FieldHint>
              </label>
              <ThemeStrip value={form.interiorTheme} onChange={(t) => p.onChange("interiorTheme", t)} name={`${id}-theme`} />
            </section>
          </div>

          <aside className="ss-aside" aria-label="Live preview">
            <div className="ss-aside-head">
              <span className="ss-h">Live preview</span>
              <span className="hint">How your store looks in town once it's live.</span>
            </div>
            <StorePreview propertyId={view.propertyId} model={preview} address={view.address} />
          </aside>
        </div>

        <p className="hint ss-more" style={{ margin: 0 }}>
          Billboards, sponsored tasks and statistics are in the <Link to="/advertise">advertiser tools</Link>.
        </p>
      </div>
    </Dialog>
  );
}

/**
 * "Re-use your store design" (ADR-0031): the owner's own design from an earlier store, offered while this store shows no
 * design of theirs. One press fills the screen; the note then says whether Save puts it up at once or it is checked first.
 */
function ReuseDesignBanner({ view, form, newLogoUploadId, disabled, onReuse }: { view: StoreSetupView; form: StoreSetupForm; newLogoUploadId: string | null; disabled: boolean; onReuse?(): void }) {
  const r = view.reuse;
  if (!r) return null;
  if (form.reusedFrom) {
    return (
      <p className="notice notice-success small ss-reuse" role="status" style={{ margin: 0 }} data-testid="store-setup-reused">
        {reuseNote(form, view, newLogoUploadId)}
      </p>
    );
  }
  if (!onReuse) return null;
  const host = r.website ? checkDestinationUrl(r.website) : null;
  return (
    <section className="notice notice-info ss-reuse" aria-label="Re-use your store design" data-testid="store-setup-reuse-banner">
      <strong>{storeReuseQuestion(r)}</strong>
      <span className="small">
        {[r.businessName, host && host.ok && !host.empty ? host.host : null, r.logo ? "your logo" : null, "the sign inside and the interior style"].filter(Boolean).join(" · ")}
        {r.offers > 0 ? `. Then your ${r.offers === 1 ? "offer" : `${r.offers} offers`} from there too.` : "."}
      </span>
      <div className="row">
        <button type="button" className="btn btn-primary btn-sm" onClick={onReuse} disabled={disabled} data-testid="store-setup-reuse">
          Re-use this design
        </button>
        <span className="hint">Fills in this screen. It was already checked, so Save puts it up right away.</span>
      </div>
    </section>
  );
}

function FieldHint({ id, error, children }: { id: string; error: string | undefined; children: ReactNode }) {
  return (
    <span id={id} className={error ? "hint error-text" : "hint"}>
      {error ?? children}
    </span>
  );
}

/** The one plain status (also shown in the store panel). */
export function StatusBanner({ id, status, copy, compact = false }: { id?: string; status: Pick<StoreSetupStatus, "state">; copy: ReturnType<typeof storeStatusCopy>; compact?: boolean }) {
  return (
    <div id={id} className={`ss-status ss-status-${copy.tone}${compact ? " is-compact" : ""}`} role="status" aria-live="polite" data-testid="store-setup-status" data-state={status.state}>
      <span className="ss-status-dot" aria-hidden="true" />
      <span className="ss-status-text">
        <strong className="ss-status-title">{copy.title}</strong>
        <span className="ss-status-detail">{copy.detail}</span>
      </span>
    </div>
  );
}

/** Drop a logo on the zone or choose a file; the secure pipeline does the rest (intent -> content -> check -> ready). */
function LogoDrop({
  upload,
  keptUrl,
  newUrl,
  onRemove,
  error,
  disabled,
}: {
  upload: Pick<UploadHandle, "state" | "start" | "cancel" | "retry">;
  keptUrl: string | null;
  newUrl: string | null;
  onRemove(): void;
  error: string | undefined;
  disabled: boolean;
}) {
  const inputId = useId();
  const [over, setOver] = useState(false);
  const s: UploadState = upload.state;
  const busy = UPLOAD_BUSY.has(s.phase);
  const bad = s.phase === "rejected" || s.phase === "rejected_local" || s.phase === "expired" || s.phase === "failed";
  const shown = newUrl ?? (keptUrl && isSameOriginApiImage(keptUrl) ? keptUrl : null);
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setOver(false);
    if (busy || disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) upload.start(file);
  };
  return (
    <div
      className={`ss-drop${over ? " is-over" : ""}${bad ? " is-bad" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        if (!busy && !disabled) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      data-testid="store-setup-logo"
      data-phase={s.phase}
    >
      <span className="ss-drop-tile" aria-hidden={shown ? undefined : true}>
        {shown ? <img src={shown} alt="Your logo" draggable={false} /> : <span className="ss-drop-plus">+</span>}
      </span>
      <div className="ss-drop-body">
        <strong>Logo or icon</strong>
        <span className={`small ${bad ? "error-text" : s.phase === "ready" ? "success-text" : "muted"}`} role={bad ? "alert" : "status"} aria-live="polite" data-testid="store-setup-logo-status">
          {s.fileName && s.phase !== "idle" && s.phase !== "ready" ? <span className="mono">{s.fileName}: </span> : null}
          {logoStatusText(s, Boolean(keptUrl))}
        </span>
        {s.phase === "uploading" ? <progress className="upload-progress" max={100} value={Math.round(s.progress * 100)} aria-label="Uploading your logo" /> : null}
        <div className="row ss-drop-actions">
          <label className={`btn btn-sm${busy || disabled ? " is-disabled" : ""}`} htmlFor={inputId} aria-disabled={busy || disabled}>
            {shown ? "Choose another file" : "Choose a file"}
          </label>
          <input
            id={inputId}
            className="ss-file"
            type="file"
            accept={UPLOAD_ACCEPT}
            disabled={busy || disabled}
            data-testid="store-setup-logo-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) upload.start(file);
            }}
          />
          {busy && s.phase !== "processing" ? (
            <button type="button" className="btn btn-sm" onClick={upload.cancel}>
              Cancel upload
            </button>
          ) : null}
          {!busy && s.retryable ? (
            <button type="button" className="btn btn-sm" onClick={upload.retry}>
              Try again
            </button>
          ) : null}
          {shown && !busy ? (
            <button type="button" className="btn btn-sm btn-ghost" onClick={onRemove} data-testid="store-setup-logo-remove">
              Remove logo
            </button>
          ) : null}
        </div>
        {error ? (
          <span className="small error-text" role="alert">
            {error}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** The interior presets as one compact row of thumbnails (the same list as the full picker). */
function ThemeStrip({ value, onChange, name }: { value: StoreThemeId; onChange(t: StoreThemeId): void; name: string }) {
  const current = STORE_THEME_OPTIONS.find((o) => o.id === value);
  const rowRef = useRef<HTMLDivElement>(null);
  // Keep the chosen preset in view (the row scrolls sideways); only the row moves, never the dialog.
  useEffect(() => {
    const row = rowRef.current;
    const on = row?.querySelector<HTMLElement>(".ss-theme.is-on");
    if (!row || !on) return;
    const left = on.offsetLeft - row.offsetLeft;
    if (left < row.scrollLeft || left + on.offsetWidth > row.scrollLeft + row.clientWidth) row.scrollLeft = Math.max(0, left - (row.clientWidth - on.offsetWidth) / 2);
  }, [value]);
  return (
    <fieldset className="ss-themes">
      <legend>
        Interior style <span className="ss-opt">free, any time</span>
      </legend>
      <div className="ss-theme-row" role="radiogroup" aria-label="Interior style" ref={rowRef}>
        {STORE_THEME_OPTIONS.map((o) => (
          <label key={o.id} className={`ss-theme${o.id === value ? " is-on" : ""}`} title={o.description}>
            <input type="radio" name={name} value={o.id} checked={o.id === value} onChange={() => onChange(o.id)} data-testid={`store-setup-theme-${o.id}`} />
            <StoreThemeThumbnail themeId={o.id} />
            <span className="ss-theme-label">{o.label}</span>
          </label>
        ))}
      </div>
      <span className="hint">{current ? `${current.label}: ${current.description}` : ""}</span>
    </fieldset>
  );
}
